import { LOOP_NONE, type Instrument, type Sample } from '../types';

/**
 * Per-channel replayer state.
 *
 * Effect "memory" fields are the reason this struct is so wide: almost every
 * tracker command reuses its last parameter when given zero, and each command
 * remembers separately. Sharing one memory slot between commands is the classic
 * way to make a module play *almost* right.
 */
export class Channel {
  index = 0;

  // --- Note / instrument ------------------------------------------------
  instrument: Instrument | null = null;
  sample: Sample | null = null;
  instrumentIndex = 0;
  /** 0-based note, before the sample's relative-note offset. */
  note = 0;
  /** Note as written in the pattern, kept for retriggers and Lxx. */
  triggerNote = 0;
  finetune = 0;
  relativeNote = 0;

  // --- Pitch --------------------------------------------------------------
  /** Base period before per-tick modulation. */
  period = 0;
  /** Period actually sounded this tick (vibrato/arpeggio applied). */
  outPeriod = 0;
  /**
   * Set by any effect that bends pitch away from `period` for this tick only.
   * Without it a stale `outPeriod` would leak into ticks where no modulating
   * effect ran, holding the channel at the last vibrato offset.
   */
  pitchModulated = false;
  portaTarget = 0;
  frequency = 0;

  // --- Volume / panning ---------------------------------------------------
  /** 0..64 channel volume as set by the pattern. */
  volume = 0;
  /** 0..1 final linear gain handed to the mixer. */
  outVolume = 0;
  /** 0..255 */
  panning = 128;
  /** 0..1 final pan handed to the mixer. */
  outPanning = 0.5;

  // --- Sample playback ----------------------------------------------------
  playing = false;
  /** Fractional read position in frames. */
  position = 0;
  /** +1 forward, -1 while a ping-pong loop is running backwards. */
  direction = 1;
  /** Gains the mixer ramps from, to keep every parameter change click-free. */
  rampVolumeL = 0;
  rampVolumeR = 0;
  rampInit = false;

  // --- Envelopes (XM) -----------------------------------------------------
  volumeEnvelopeTick = 0;
  volumeEnvelopeValue = 64;
  volumeEnvelopeDone = false;
  panningEnvelopeTick = 0;
  panningEnvelopeValue = 32;
  panningEnvelopeDone = false;
  keyOff = false;
  fadeoutVolume = 65536;

  // --- Autovibrato (XM instrument level) ---------------------------------
  autoVibratoPos = 0;
  autoVibratoSweep = 0;

  // --- Effect memory ------------------------------------------------------
  memPortaUp = 0;
  memPortaDown = 0;
  memTonePorta = 0;
  memVibratoSpeed = 0;
  memVibratoDepth = 0;
  memTremoloSpeed = 0;
  memTremoloDepth = 0;
  memVolumeSlide = 0;
  memGlobalVolumeSlide = 0;
  memPanningSlide = 0;
  memFinePortaUp = 0;
  memFinePortaDown = 0;
  memExtraFinePortaUp = 0;
  memExtraFinePortaDown = 0;
  memFineVolumeUp = 0;
  memFineVolumeDown = 0;
  memSampleOffset = 0;
  memRetrig = 0;
  memTremor = 0;
  memArpeggio = 0;

  // --- Oscillators --------------------------------------------------------
  vibratoPos = 0;
  vibratoWaveform = 0;
  /** When set, the waveform does not reset on a new note. */
  vibratoNoRetrig = false;
  tremoloPos = 0;
  tremoloWaveform = 0;
  tremoloNoRetrig = false;
  /** Volume offset from tremolo, applied on top of `volume`. */
  tremoloVolume = 0;

  // --- Misc effect state --------------------------------------------------
  glissando = false;
  tremorCount = 0;
  tremorOn = false;
  /** Separate from `tremorOn` so a channel not using Txy is never gated off. */
  tremorMute = false;
  patternLoopRow = 0;
  patternLoopCount = 0;
  /** Set by EDx so later ticks know a note is still pending. */
  noteDelayed = false;
  delayedNote = 0;
  delayedInstrument = 0;
  delayedVolumeColumn = 0;
  /** ProTracker's EFx invert-loop ("funk repeat") state. */
  funkSpeed = 0;
  funkPos = 0;
  funkOffset = 0;

  reset(index: number, panning: number): void {
    this.index = index;
    this.instrument = null;
    this.sample = null;
    this.instrumentIndex = 0;
    this.note = 0;
    this.triggerNote = 0;
    this.finetune = 0;
    this.relativeNote = 0;
    this.period = 0;
    this.outPeriod = 0;
    this.pitchModulated = false;
    this.portaTarget = 0;
    this.frequency = 0;
    this.volume = 0;
    this.outVolume = 0;
    this.panning = panning;
    this.outPanning = panning / 255;
    this.playing = false;
    this.position = 0;
    this.direction = 1;
    this.rampVolumeL = 0;
    this.rampVolumeR = 0;
    this.rampInit = false;
    this.volumeEnvelopeTick = 0;
    this.volumeEnvelopeValue = 64;
    this.volumeEnvelopeDone = false;
    this.panningEnvelopeTick = 0;
    this.panningEnvelopeValue = 32;
    this.panningEnvelopeDone = false;
    this.keyOff = false;
    this.fadeoutVolume = 65536;
    this.autoVibratoPos = 0;
    this.autoVibratoSweep = 0;
    this.memPortaUp = 0;
    this.memPortaDown = 0;
    this.memTonePorta = 0;
    this.memVibratoSpeed = 0;
    this.memVibratoDepth = 0;
    this.memTremoloSpeed = 0;
    this.memTremoloDepth = 0;
    this.memVolumeSlide = 0;
    this.memGlobalVolumeSlide = 0;
    this.memPanningSlide = 0;
    this.memFinePortaUp = 0;
    this.memFinePortaDown = 0;
    this.memExtraFinePortaUp = 0;
    this.memExtraFinePortaDown = 0;
    this.memFineVolumeUp = 0;
    this.memFineVolumeDown = 0;
    this.memSampleOffset = 0;
    this.memRetrig = 0;
    this.memTremor = 0;
    this.memArpeggio = 0;
    this.vibratoPos = 0;
    this.vibratoWaveform = 0;
    this.vibratoNoRetrig = false;
    this.tremoloPos = 0;
    this.tremoloWaveform = 0;
    this.tremoloNoRetrig = false;
    this.tremoloVolume = 0;
    this.glissando = false;
    this.tremorCount = 0;
    this.tremorOn = false;
    this.tremorMute = false;
    this.patternLoopRow = 0;
    this.patternLoopCount = 0;
    this.noteDelayed = false;
    this.delayedNote = 0;
    this.delayedInstrument = 0;
    this.delayedVolumeColumn = 0;
    this.funkSpeed = 0;
    this.funkPos = 0;
    this.funkOffset = 0;
  }

  /** Stop the voice outright — used by note cut and by out-of-range offsets. */
  stop(): void {
    this.playing = false;
    this.outVolume = 0;
  }

  /** True when there is audible sample data to render. */
  get active(): boolean {
    return this.playing && this.sample !== null && this.sample.length > 0;
  }

  /** Whether the current sample loops, and therefore never runs out. */
  get looping(): boolean {
    return this.sample !== null && this.sample.loopType !== LOOP_NONE && this.sample.loopLength > 0;
  }
}
